/** Shared LootDrop WebSocket protocol types. */

export type LdPhase = "active" | "ended";

export type LdRoomListEntry = {
  code: string;
  hostName: string;
  hostAccountId: string;
  playerCount: number;
  maxCursors: number;
  onlyFriends: boolean;
  canJoin: boolean;
};

export type LdDropWire = {
  id: string;
  amount: number;
  x: number;
  y: number;
  /** When set, this drop awards a shop cosmetic instead of Dots. */
  cosmeticId?: string;
  cosmeticName?: string;
  cosmeticImageData?: string;
};

export type LdPickupFxWire = {
  id: string;
  amount: number;
  x: number;
  y: number;
  playerId: string;
  playerName: string;
  cosmeticId?: string;
  cosmeticName?: string;
};

export type LdPlayerWire = {
  id: string;
  name: string;
  isHost: boolean;
  x: number;
  y: number;
  accountId?: string;
  equippedCosmeticIds?: string[];
  /** Player's Inventory/Settings cursor fill color. */
  cursorColor?: string;
  /** Dots earned from catches in this room (excludes host). */
  dotsEarned?: number;
};

export type LdRoomSnapshot = {
  code: string;
  phase: LdPhase;
  hostId: string;
  hostName: string;
  hostDotsBalance: number;
  maxCursors: number;
  onlyFriends: boolean;
  freezeDots: boolean;
  /** Max Dots the host may launch across the whole session. `0` = no limit. */
  sessionDotsLimit: number;
  /**
   * Net Dots currently committed to this session (launched − uncollected refunds).
   * Against `sessionDotsLimit` when that limit is > 0.
   */
  sessionDotsSpent: number;
  players: LdPlayerWire[];
  drops: LdDropWire[];
  pickupFx: LdPickupFxWire[];
};

/** Client → server */
export type LdClientMessage =
  | { type: "list_rooms"; accountId?: string; friendAccountIds?: string[] }
  | {
      type: "create_room";
      name: string;
      accountId: string;
      maxCursors: number;
      onlyFriends: boolean;
      /** `0` = no session limit. */
      sessionDotsLimit: number;
      /** Client-reported balance; used when DB is unavailable, otherwise DB wins. */
      dotsBalance?: number;
      equippedCosmeticIds?: string[];
      cursorColor?: string;
    }
  | {
      type: "join_room";
      code: string;
      name: string;
      accountId: string;
      dotsBalance?: number;
      equippedCosmeticIds?: string[];
      cursorColor?: string;
    }
  | { type: "leave_room" }
  | { type: "end_room" }
  | {
      type: "spawn_drop";
      amount: number;
      x: number;
      y: number;
      cosmeticId?: string;
      cosmeticName?: string;
      cosmeticImageData?: string;
    }
  | { type: "set_freeze_dots"; enabled: boolean }
  | { type: "rain_launch"; ballCount: number; amountPerBall: number }
  | { type: "kick_player"; playerId: string }
  | { type: "block_player"; playerId: string }
  | { type: "pos"; x: number; y: number; seq: number };

/** Server → client */
export type LdServerMessage =
  | { type: "connected"; playerId: string }
  | { type: "rooms_list"; rooms: LdRoomListEntry[] }
  | { type: "room_state"; room: LdRoomSnapshot }
  | { type: "session_state"; state: LdSessionStateSnapshot }
  | { type: "dots_updated"; accountId: string; balance: number; delta: number }
  | {
      type: "item_awarded";
      accountId: string;
      cosmeticId: string;
      cosmeticName: string;
      dropId: string;
    }
  | { type: "session_ended"; reason: string; hostDotsBalance?: number; finalDotsBalance?: number }
  | { type: "kicked"; reason?: string; finalDotsBalance?: number }
  | { type: "error"; message: string };

export type LdSessionStateSnapshot = {
  tick: number;
  hostDotsBalance: number;
  freezeDots: boolean;
  sessionDotsLimit: number;
  sessionDotsSpent: number;
  players: Array<{
    id: string;
    x: number;
    y: number;
    isHost: boolean;
    dotsEarned?: number;
  }>;
  drops: LdDropWire[];
  pickupFx: LdPickupFxWire[];
};

export const LD_WS_PATH = "/ws/loot";
export const LD_ROOM_CODE_LEN = 5;
export const LD_MIN_MAX_CURSORS = 1;
export const LD_MAX_MAX_CURSORS = 32;
export const LD_MAX_DROP_AMOUNT = 1_000_000;
export const LD_MIN_SESSION_DOTS_LIMIT = 0;
export const LD_MAX_SESSION_DOTS_LIMIT = 100_000_000;
export const LD_DROP_FALL_SPEED = 0.12;
export const LD_COLLECT_RADIUS_NORM = 0.035;
export const LD_RAIN_MAX_CONCURRENT = 10;
export const LD_RAIN_MAX_BALLS = 500;
export const LD_RAIN_SPAWN_INTERVAL_SEC = 0.14;
export const LD_MAX_COSMETIC_IMAGE_CHARS = 400_000;

/** Normalize user-provided or default WebSocket URL for LootDrops. */
export function ldWsUrl(): string {
  const env = (import.meta as { env?: { VITE_LD_WS_URL?: string; VITE_MP_WS_URL?: string } }).env;
  let url = env?.VITE_LD_WS_URL?.trim() || env?.VITE_MP_WS_URL?.trim() || defaultLdWsUrl();
  if (url.startsWith("https://")) url = `wss://${url.slice(8)}`;
  else if (url.startsWith("http://")) url = `ws://${url.slice(7)}`;
  if (!url.includes("/ws/")) {
    url = `${url.replace(/\/$/, "")}${LD_WS_PATH}`;
  } else if (!url.includes(LD_WS_PATH)) {
    url = url.replace(/\/ws\/[^/]*$/, LD_WS_PATH);
  }
  return url;
}

function defaultLdWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${LD_WS_PATH}`;
}

export function ldConnectionTimeoutMs(url: string): number {
  if (/localhost|127\.0\.0\.1/.test(url)) return 12_000;
  if (/onrender\.com|railway\.app|fly\.dev/i.test(url)) return 90_000;
  return 25_000;
}

export function ldIsRemoteWsUrl(url: string): boolean {
  return !/localhost|127\.0\.0\.1/.test(url);
}
